/**
 * What the customer decided about credit on ONE order, remembered for the
 * browser session, so the delivery step can tick "use my credit" for them
 * exactly once and never tick it back after they untick it.
 *
 * sessionStorage, per order id: it has to survive the delivery step
 * re-mounting (Back / Next, a reload) but has no business outliving the
 * session or crossing into another order. Every access is guarded — private
 * windows and blocked storage throw, and a thrown read must degrade to
 * "no decision recorded", never break the checkout.
 */
const KEY = (kind: "optout" | "auto", orderId: string) =>
  `credit_${kind}:${orderId}`;

function read(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function write(key: string, on: boolean): void {
  try {
    if (on) window.sessionStorage.setItem(key, "1");
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: the choice lasts only as long as the page does.
  }
}

/** The customer unticked "use my credit" on this order. */
export const hasOptedOut = (orderId: string) => read(KEY("optout", orderId));
export const setOptedOut = (orderId: string, on: boolean) =>
  write(KEY("optout", orderId), on);

/** Credit was already applied automatically once on this order. */
export const wasAutoApplied = (orderId: string) => read(KEY("auto", orderId));
export const markAutoApplied = (orderId: string) =>
  write(KEY("auto", orderId), true);
/**
 * Lets auto-apply fire once more on this order: a gift-card code redeemed on
 * it adds money the earlier auto-apply never saw, and "once per order" means
 * once per balance the customer brought, not once ever.
 */
export const clearAutoApplied = (orderId: string) =>
  write(KEY("auto", orderId), false);

